import { isPhoneComplete } from '@/utils/phoneMask'

export const WEEKLY_NORM_HOURS = [40, 36, 30, 20]
export const DEFAULT_WEEKLY_NORM_HOURS = 40

export function normalizeWeeklyNormHours(value) {
  const n = Number(value)
  return WEEKLY_NORM_HOURS.includes(n) ? n : DEFAULT_WEEKLY_NORM_HOURS
}

export function isEmployeeProfileReady(data) {
  return Boolean(
    String(data?.m_name || '').trim() &&
    data?.positionId &&
    isPhoneComplete(data?.phone)
  )
}

/**
 * Сотрудники на посте (привязки employee_car_places) для календаря и сайдпанели.
 */

export const SCHEDULE_HEADER_VISIBLE_EMPLOYEES = 2

/** Сколько человек спрятано за «+N» в шапке поста. */
export function scheduleHeaderOverflowCount(
  total,
  visible = SCHEDULE_HEADER_VISIBLE_EMPLOYEES
) {
  const n = Number(total) || 0
  return Math.max(0, n - visible)
}

export function formatEmployeeFullName(emp) {
  return [emp?.f_name, emp?.m_name, emp?.l_name]
    .filter(Boolean)
    .join(' ')
    .trim()
}

/** Подпись без ФИО: «Сотрудник №57». `t` — i18n, иначе русский fallback. */
export function unnamedEmployeeName(id, t) {
  if (id == null || id === '') return ''
  if (typeof t === 'function') {
    return t('pages.calendar.side_panel.employee_unnamed', { id })
  }
  return `Сотрудник №${id}`
}

export function employeeVisibleName(emp, t) {
  return formatEmployeeFullName(emp) || unnamedEmployeeName(emp?.id, t)
}

export function employeeCarPlaceIds(emp) {
  if (Array.isArray(emp?.carPlaceIds)) {
    return emp.carPlaceIds.map((id) => Number(id))
  }

  return (emp?.carPlaces || []).map((link) => Number(link.carPlaceId))
}

export function employeeServiceIds(emp) {
  if (!Array.isArray(emp?.serviceIds)) return null
  return emp.serviceIds.map((id) => Number(id))
}

/** Уникальные serviceIds сотрудников (онлайн-каталог без выбранного человека). */
export function collectEmployeesServiceIds(employees) {
  const ids = new Set()
  for (const emp of employees || []) {
    const list = employeeServiceIds(emp)
    if (!list) continue
    for (const id of list) {
      if (Number.isFinite(id) && id > 0) ids.add(id)
    }
  }
  return [...ids]
}

/** Доп. шаг онлайн-записи выбирает сотрудника (график + включённый доп. шаг). */
export function extraStepSelectsEmployee({
  scheduleEnabled,
  allowPostSelectionOnline,
} = {}) {
  return scheduleEnabled === true && allowPostSelectionOnline === true
}

export function isEmployeeAssignedToPost(emp, postId) {
  if (emp == null || postId == null || postId === 'all') return false

  return employeeCarPlaceIds(emp).includes(Number(postId))
}

/** Фильтр «Все посты» / конкретный пост: любая привязка, не только дефолтный. */
export function employeeMatchesWorkplaceFilter(emp, workplaceId) {
  if (workplaceId == null || workplaceId === '' || workplaceId === 'ALL') {
    return true
  }

  return isEmployeeAssignedToPost(emp, workplaceId)
}

/**
 * Сотрудник в выбранном филиале: привязка employee_department
 * или хотя бы один пост этого филиала.
 */
export function employeeBelongsToDepartment(
  emp,
  departmentId,
  departmentPostIds
) {
  if (departmentId == null || departmentId === '') return true

  const dept = String(departmentId)
  const departmentIds = emp?.departmentIds || []
  const carPlaceIds = emp?.carPlaceIds || []

  if (departmentIds.length === 0 && carPlaceIds.length === 0) return true

  if (departmentIds.some((id) => String(id) === dept)) return true

  const posts =
    departmentPostIds instanceof Set
      ? departmentPostIds
      : new Set([...(departmentPostIds || [])].map((id) => String(id)))

  return carPlaceIds.some((id) => posts.has(String(id)))
}

export function employeesAssignedToPost(employees, postId) {
  return (employees || []).filter((emp) =>
    isEmployeeAssignedToPost(emp, postId)
  )
}

export function mapScheduleEmployee(row, t) {
  return {
    id: row.id,
    name: employeeVisibleName(row, t),
    carPlaceIds: employeeCarPlaceIds(row),
    primaryWorkplaceId: row.defaultCarPlaceId ?? row.primaryWorkplaceId ?? null,
    serviceIds: Array.isArray(row.serviceIds)
      ? row.serviceIds.map((id) => Number(id))
      : [],
    color: row.color || null,
  }
}

/**
 * Посты только выбранных филиалов. Без филиалов — пустой список.
 */
export function filterWorkplacesByDepartments(workplaces, departmentIds) {
  const ids = (departmentIds || [])
    .map((id) => String(id))
    .filter((id) => id !== '')

  if (!ids.length) return []

  const allowed = new Set(ids)
  return (workplaces || []).filter((wp) =>
    allowed.has(String(wp?.departmentId))
  )
}

/**
 * Подпись поста: с филиалом "Касса №1 (Центр)", без филиала — только name.
 * @param {object} workplace
 * @param {{ withBranch?: boolean }} [options] withBranch=false — не писать филиал в скобках
 */
export function formatWorkplaceOptionLabel(
  workplace,
  { withBranch = true } = {}
) {
  const name = workplace?.name || ''
  const branch = workplace?.departmentName || ''
  if (!name) return branch
  if (!withBranch || !branch) return name
  return `${name} (${branch})`
}

export const MIN_STAFF_PER_DAY = 1
export const MAX_STAFF_PER_DAY = 10

export function clampMinStaffPerDay(value) {
  const n = Number(value)
  if (!Number.isInteger(n) || n < MIN_STAFF_PER_DAY) return MIN_STAFF_PER_DAY
  return Math.min(MAX_STAFF_PER_DAY, n)
}

export function mapLinkedIds(rows) {
  return (rows || [])
    .map((row) => row?.id)
    .filter((id) => id != null && id !== '')
}

export function toSelectOptions(items) {
  return (items || []).map((item) => ({
    value: item?.id,
    name: item?.name || '',
  }))
}

export function pickSelectedOptions(options, ids) {
  const selected = new Set((ids || []).map(String))
  return (options || []).filter((opt) => selected.has(String(opt?.value)))
}
